<template>
    <div class="trends-carousel-wrapper" :class="{'animate-in': animate}">
        <slot name="before" />
        <Swiper
            :modules="swiperModules"
            :slides-per-view="'auto'"
            :navigation="{nextEl: `.${navigationNextClass}`, prevEl: `.${navigationPrevClass}`, hiddenClass: 'swiper-button-hidden'}"
            :pagination="{clickable: true, el: `.${paginationClass}`}"
            :space-between="spacebetween"
            :breakpoints="breakpoints"
            @swiper="onSwiper"
            @resize="onResize"
        >
            <SwiperSlide v-for="(item, i) in items" :key="i">
                <slot name="card" :item="item" />
            </SwiperSlide>
        </Swiper>
        <slot name="after" />
        <div class="trends-carousel__buttons" :class="{'swiper-navigation-hidden': isNavigationHidden}">
            <VJoyIconButton :class="['swiper-button', 'swiper-button-prev', navigationPrevClass]" icon="chevron-left" color="primary" size="small" />
            <VJoyIconButton :class="['swiper-button', 'swiper-button-next', navigationNextClass]" icon="chevron-right" color="primary" size="small" />
        </div>
        <div :class="['trends-carousel__pagination', 'swiper-pagination', paginationClass, {'swiper-pagination-hidden': isNavigationHidden}]" />
    </div>
</template>
<script setup lang="ts" generic="T">
    // @ts-expect-error No types for Pagination & Navigation
    import {Navigation, Pagination} from 'swiper';
    import type {Swiper as SwiperClass} from 'swiper/types';
    import {Swiper, SwiperSlide} from 'swiper/vue';
    import {ref, shallowRef, useId} from '#imports';
    import {VJoyIconButton} from '@maltjoy/core-vue';

    export type TrendsCarouselBreakpoints = Record<number, {slidesPerView: number}>;
    type TrendsCarouselProps = {
        items: T[];
        breakpoints?: TrendsCarouselBreakpoints;
        spacebetween?: number;
    };
    withDefaults(defineProps<TrendsCarouselProps>(), {
        breakpoints: () => ({}),
    });

    const swiperModules = [Navigation, Pagination];

    const {navigationPrevClass, navigationNextClass, paginationClass} = useUniqueSelectors();
    const {animate} = useAnimate();
    const {isNavigationHidden, onSwiper, onResize} = useSwiperNavigation();

    /**
     * Ensure unique selectors per instance to be able to use multiple carousels on the same page.
     */
    function useUniqueSelectors() {
        const instanceId = useId().replace(/:/g, '');
        const navigationPrevClass = `trends-carousel-nav-prev--${instanceId}`;
        const navigationNextClass = `trends-carousel-nav-next--${instanceId}`;
        const paginationClass = `trends-carousel-pagination--${instanceId}`;

        return {
            navigationPrevClass,
            navigationNextClass,
            paginationClass,
        };
    }
    function useSwiperNavigation() {
        const swiperRef = shallowRef<SwiperClass>();
        const isNavigationHidden = ref(false);

        function onSwiper(swiper: SwiperClass) {
            swiperRef.value = swiper;
            computeIsNavigationHidden();
        }

        function onResize(swiper: SwiperClass) {
            swiperRef.value = swiper;
            computeIsNavigationHidden();
        }

        function computeIsNavigationHidden() {
            isNavigationHidden.value = !!swiperRef.value && swiperRef.value.isBeginning && swiperRef.value.isEnd;
        }

        return {
            onSwiper,
            onResize,
            isNavigationHidden,
        };
    }
    function useAnimate() {
        const animate = ref(false);

        return {
            animate,
        };
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .trends-carousel-wrapper {
        .swiper {
            margin: 0 calc(-1 * var(--joy-core-spacing-3));
            padding: 0 var(--joy-core-spacing-3);
        }
        .swiper-wrapper {
            display: flex;
            align-items: stretch;
        }
        .swiper-slide {
            width: 280px;
            height: auto;
            padding: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-7);

            &:not(:last-child) {
                margin-right: var(--joy-core-spacing-5);
            }

            > div {
                height: 100%;
                > div {
                    height: 100%;
                    > div {
                        height: 100%;
                    }
                }
            }
        }

        .swiper-pagination-bullet {
            margin: 0 4px;
        }

        .swiper-navigation-hidden,
        .swiper-pagination-hidden {
            display: none !important;
        }

        .trends-carousel {
            position: relative;
            display: flex;
            -webkit-tap-highlight-color: transparent;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            opacity: 1;
            padding: 0;

            @include mq.screen_md() {
                margin: 0 calc(-1 * var(--joy-core-spacing-6));
            }

            &.swiper-ready {
                opacity: 1;
            }
        }

        .trends-carousel__buttons {
            width: 150px;
            margin: var(--joy-core-spacing-4) auto 0;
            display: none;

            @media (hover: hover) {
                display: block;
            }

            .joy-button:not(:disabled) {
                background-color: var(--joy-color-neutral-60);
            }

            .swiper-button-prev {
                margin-right: 40px;
            }
        }

        .trends-carousel__pagination {
            height: 20px;
            width: 100%;
            display: none;

            @media (hover: none) {
                display: block;
            }

            .swiper-pagination-bullet {
                height: 10px;
                width: 10px;
                display: inline-block;
                background-color: var(--joy-color-neutral-30);
                opacity: 1;

                &-active {
                    background-color: var(--joy-color-neutral-60);
                }
            }
        }
    }
</style>
